import React, { useEffect, useMemo, useState } from "react";
import API from "../../../services/api";
import SaveStudioAssetButton from "../SaveStudioAssetButton";
import FieldHint from "../FieldHint";
import useI18n from "../../../i18n/useI18n";

import MotionSurface from "../../../motion/studio/MotionSurface";
import ToolResultSkeleton from "../../loaders/ToolResultSkeleton";

import ResultCard from "../../ui/ResultCard";
import ActionRow from "../../ui/ActionRow";
import CopyButton from "../../ui/CopyButton";

import AIActionButton from "../../ui/buttons/AIActionButton";
import UtilityButton from "../../ui/buttons/UtilityButton";

const THEME_KEY = "hookora_theme";

const platformOptions = [
  { value: "instagram", labelKey: "instagram", fallback: "Instagram" },
  { value: "tiktok", labelKey: "tiktok", fallback: "TikTok" },
  { value: "youtube", labelKey: "youtube", fallback: "YouTube" },
  { value: "x", labelKey: "x", fallback: "X / Twitter" },
  { value: "facebook", labelKey: "facebook", fallback: "Facebook" },
  { value: "linkedin", labelKey: "linkedin", fallback: "LinkedIn" },
  { value: "general", labelKey: "general", fallback: "General" },
];

const outputTypeOptions = [
  { value: "reply", labelKey: "reply", fallback: "Reply" },
  { value: "hook", labelKey: "hook", fallback: "Hook" },
  { value: "caption", labelKey: "caption", fallback: "Caption" },
  { value: "short_script", labelKey: "shortScript", fallback: "Short Script" },
  { value: "content_idea", labelKey: "contentIdea", fallback: "Content Idea" },
  { value: "carousel_angle", labelKey: "carouselAngle", fallback: "Carousel Angle" },
];

const toneOptions = [
  { value: "natural", labelKey: "natural", fallback: "Natural" },
  { value: "friendly", labelKey: "friendly", fallback: "Friendly" },
  { value: "bold", labelKey: "bold", fallback: "Bold" },
  { value: "educational", labelKey: "educational", fallback: "Educational" },
  { value: "sales", labelKey: "sales", fallback: "Sales" },
  { value: "professional", labelKey: "professional", fallback: "Professional" },
];

const getStoredTheme = () => {
  if (typeof window === "undefined") return "light";
  return localStorage.getItem(THEME_KEY) || "light";
};

const normalizeArray = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item.trim();
        if (item && typeof item === "object") {
          return (
            item.text ||
            item.content ||
            item.label ||
            item.title ||
            item.name ||
            JSON.stringify(item)
          );
        }
        return String(item ?? "").trim();
      })
      .filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split("\n")
      .map((line) => line.replace(/^[-*•\d.)\s]+/, "").trim())
      .filter(Boolean);
  }

  return [];
};

const formatResultToText = (result, t) => {
  if (!result) return "";
  if (typeof result === "string") return result;

  const sections = [];

  const mainOutput =
    result.output ||
    result.reply ||
    result.content ||
    result.generated_content ||
    result.result ||
    result.script;

  if (mainOutput) {
    sections.push(`${t("studioTools.commentToContentTool.resultLabels.output")}:\n${mainOutput}`);
  }

  const angle =
    result.angle || result.content_angle || result.idea_angle || result.strategy_angle;

  if (angle) {
    sections.push(`${t("studioTools.commentToContentTool.resultLabels.angle")}:\n${angle}`);
  }

  const summary = result.summary || result.overview || result.analysis || result.explanation;
  if (summary) {
    sections.push(`${t("studioTools.commentToContentTool.resultLabels.summary")}:\n${summary}`);
  }

  const ideas = normalizeArray(
    result.ideas || result.content_ideas || result.follow_up_ideas || result.variations
  );
  if (ideas.length) {
    sections.push(
      `${t("studioTools.commentToContentTool.resultLabels.ideas")}:\n${ideas
        .map((item) => `- ${item}`)
        .join("\n")}`
    );
  }

  const notes = normalizeArray(result.notes || result.tips || result.recommendations);
  if (notes.length) {
    sections.push(
      `${t("studioTools.commentToContentTool.resultLabels.notes")}:\n${notes
        .map((item) => `- ${item}`)
        .join("\n")}`
    );
  }

  if (!sections.length) {
    return JSON.stringify(result, null, 2);
  }

  return sections.join("\n\n");
};

const CommentToContentTool = ({
  sharedTopic,
  setSharedTopic,
  sharedPlatform,
  setSharedPlatform,
}) => {
  const { t } = useI18n();

  const [theme, setTheme] = useState(getStoredTheme());
  const [commentText, setCommentText] = useState("");
  const [contentContext, setContentContext] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [goal, setGoal] = useState("");
  const [outputType, setOutputType] = useState("reply");
  const [tone, setTone] = useState("natural");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const selectedPlatform = sharedPlatform || "general";
  const topicValue = sharedTopic || "";

  useEffect(() => {
    const syncTheme = () => setTheme(getStoredTheme());

    syncTheme();
    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-theme-change", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-theme-change", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const styles = useMemo(() => {
    return {
      wrapper: {
        display: "flex",
        flexDirection: "column",
        gap: 18,
        color: isDark ? "#f8fafc" : "#0f172a",
      },
      card: {
        background: isDark
          ? "linear-gradient(180deg, rgba(15,23,42,0.96) 0%, rgba(15,23,42,0.90) 100%)"
          : "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.16)" : "rgba(15,23,42,0.08)"}`,
        borderRadius: 20,
        padding: 20,
        boxShadow: isDark
          ? "0 10px 30px rgba(0,0,0,0.22)"
          : "0 10px 30px rgba(15,23,42,0.06)",
      },
      headerRow: {
        display: "flex",
        justifyContent: "space-between",
        gap: 16,
        alignItems: "flex-start",
        flexWrap: "wrap",
      },
      titleWrap: {
        display: "flex",
        flexDirection: "column",
        gap: 8,
        maxWidth: 780,
      },
      badge: {
        alignSelf: "flex-start",
        padding: "6px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        background: isDark ? "rgba(168,85,247,0.16)" : "rgba(168,85,247,0.10)",
        color: isDark ? "#d8b4fe" : "#7e22ce",
        border: `1px solid ${isDark ? "rgba(168,85,247,0.24)" : "rgba(168,85,247,0.18)"}`,
      },
      title: {
        margin: 0,
        fontSize: 22,
        fontWeight: 800,
        letterSpacing: "-0.02em",
      },
      subtitle: {
        margin: 0,
        fontSize: 14,
        lineHeight: 1.6,
        color: isDark ? "rgba(226,232,240,0.78)" : "rgba(15,23,42,0.68)",
      },
      formGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
        gap: 16,
        marginTop: 18,
      },
      fieldWrap: {
        display: "flex",
        flexDirection: "column",
        gap: 8,
      },
      fullWidthField: {
        display: "flex",
        flexDirection: "column",
        gap: 8,
        gridColumn: "1 / -1",
      },
      label: {
        fontSize: 13,
        fontWeight: 700,
        color: isDark ? "#e2e8f0" : "#0f172a",
      },
      input: {
        width: "100%",
        borderRadius: 14,
        border: `1px solid ${isDark ? "rgba(148,163,184,0.18)" : "rgba(15,23,42,0.12)"}`,
        background: isDark ? "rgba(15,23,42,0.72)" : "#ffffff",
        color: isDark ? "#f8fafc" : "#0f172a",
        padding: "13px 14px",
        outline: "none",
        fontSize: 14,
        boxSizing: "border-box",
      },
      textarea: {
        width: "100%",
        minHeight: 140,
        resize: "vertical",
        borderRadius: 14,
        border: `1px solid ${isDark ? "rgba(148,163,184,0.18)" : "rgba(15,23,42,0.12)"}`,
        background: isDark ? "rgba(15,23,42,0.72)" : "#ffffff",
        color: isDark ? "#f8fafc" : "#0f172a",
        padding: 14,
        outline: "none",
        fontSize: 14,
        lineHeight: 1.55,
        boxSizing: "border-box",
      },
      actionsRow: {
        display: "flex",
        gap: 12,
        flexWrap: "wrap",
        marginTop: 18,
      },
      primaryButton: {
        border: "none",
        borderRadius: 14,
        padding: "12px 18px",
        fontSize: 14,
        fontWeight: 800,
        cursor: loading ? "not-allowed" : "pointer",
        background: loading
          ? isDark
            ? "rgba(168,85,247,0.45)"
            : "rgba(168,85,247,0.45)"
          : isDark
          ? "linear-gradient(135deg, #a855f7 0%, #ec4899 100%)"
          : "linear-gradient(135deg, #9333ea 0%, #db2777 100%)",
        color: "#ffffff",
        boxShadow: loading
          ? "none"
          : isDark
          ? "0 12px 24px rgba(168,85,247,0.20)"
          : "0 12px 24px rgba(147,51,234,0.16)",
      },
      secondaryButton: {
        borderRadius: 14,
        padding: "12px 18px",
        fontSize: 14,
        fontWeight: 700,
        cursor: "pointer",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.18)" : "rgba(15,23,42,0.12)"}`,
        background: isDark ? "rgba(30,41,59,0.82)" : "#ffffff",
        color: isDark ? "#e2e8f0" : "#0f172a",
      },
      mutedText: {
        fontSize: 13,
        color: isDark ? "rgba(226,232,240,0.68)" : "rgba(15,23,42,0.62)",
      },
      errorBox: {
        marginTop: 16,
        padding: 14,
        borderRadius: 14,
        background: isDark ? "rgba(127,29,29,0.25)" : "rgba(254,226,226,1)",
        border: `1px solid ${isDark ? "rgba(248,113,113,0.22)" : "rgba(248,113,113,0.28)"}`,
        color: isDark ? "#fecaca" : "#991b1b",
        fontSize: 14,
        lineHeight: 1.5,
      },
      resultCard: {
        background: isDark ? "rgba(2,6,23,0.56)" : "rgba(248,250,252,0.96)",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`,
        borderRadius: 18,
        padding: 18,
        display: "flex",
        flexDirection: "column",
        gap: 16,
      },
      resultHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
      },
      resultTitle: {
        margin: 0,
        fontSize: 18,
        fontWeight: 800,
      },
      resultActions: {
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
        alignItems: "center",
      },
      section: {
        display: "flex",
        flexDirection: "column",
        gap: 10,
      },
      sectionTitle: {
        margin: 0,
        fontSize: 14,
        fontWeight: 800,
        color: isDark ? "#f1f5f9" : "#0f172a",
      },
      paragraph: {
        margin: 0,
        fontSize: 14,
        lineHeight: 1.75,
        color: isDark ? "rgba(226,232,240,0.86)" : "rgba(15,23,42,0.82)",
        whiteSpace: "pre-wrap",
      },
      list: {
        margin: 0,
        paddingLeft: 18,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        color: isDark ? "rgba(226,232,240,0.86)" : "rgba(15,23,42,0.82)",
        fontSize: 14,
        lineHeight: 1.65,
      },
      chipRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: 10,
      },
      chip: {
        padding: "7px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        background: isDark ? "rgba(51,65,85,0.88)" : "#ffffff",
        color: isDark ? "#cbd5e1" : "#334155",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`,
      },
      outputBox: {
        borderRadius: 16,
        padding: 16,
        background: isDark ? "rgba(15,23,42,0.85)" : "#ffffff",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.12)" : "rgba(15,23,42,0.08)"}`,
      },
    };
  }, [isDark, loading]);

  const formattedText = useMemo(() => formatResultToText(result, t), [result, t]);

  const mainOutput =
    result?.output ||
    result?.reply ||
    result?.content ||
    result?.generated_content ||
    result?.result ||
    result?.script ||
    "";

  const ideas = useMemo(
    () =>
      normalizeArray(
        result?.ideas || result?.content_ideas || result?.follow_up_ideas || result?.variations
      ),
    [result]
  );

  const notes = useMemo(
    () => normalizeArray(result?.notes || result?.tips || result?.recommendations),
    [result]
  );

  const clearResults = () => {
    setResult(null);
    setError("");
    setCopied(false);
  };

  const resetAll = () => {
    setCommentText("");
    setContentContext("");
    setTargetAudience("");
    setGoal("");
    setOutputType("reply");
    setTone("natural");
    setResult(null);
    setCopied(false);
    setError("");
    if (typeof setSharedTopic === "function") setSharedTopic("");
    if (typeof setSharedPlatform === "function") setSharedPlatform("general");
  };

  const copyResult = async () => {
    if (!formattedText) return;

    try {
      await navigator.clipboard.writeText(formattedText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setError(t("studioTools.commentToContentTool.messages.copyFailed"));
    }
  };

  const tryPost = async (paths, payload) => {
    let lastError = null;

    for (const path of paths) {
      try {
        const response = await API.post(path, payload);
        return response?.data;
      } catch (err) {
        lastError = err;
      }
    }

    throw lastError;
  };

  const handleGenerate = async () => {
    if (!commentText.trim()) {
      setError(t("studioTools.commentToContentTool.messages.commentRequired"));
      return;
    }

    setLoading(true);
    setError("");
    setCopied(false);

    const payload = {
      topic: topicValue.trim(),
      platform: selectedPlatform,
      comment: commentText.trim(),
      content_context: contentContext.trim(),
      target_audience: targetAudience.trim(),
      goal: goal.trim(),
      output_type: outputType,
      tone,
    };

    try {
      const data = await tryPost(
        [
          "/studio/comment-to-content",
          "/studio/comment_to_content",
          "/studio/comment-content",
          "/studio/comments-to-content",
        ],
        payload
      );

      const normalized =
        data?.result ||
        data?.content ||
        data?.data ||
        data?.comment_to_content ||
        data;

      setResult(normalized);
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        t("studioTools.commentToContentTool.messages.generateFailed");
      setError(message);
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MotionSurface>
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <div style={styles.headerRow}>
          <div style={styles.titleWrap}>
            <span style={styles.badge}>{t("studioTools.commentToContentTool.badge")}</span>
            <h2 style={styles.title}>{t("studioTools.commentToContentTool.title")}</h2>
            <p style={styles.subtitle}>{t("studioTools.commentToContentTool.description")}</p>
          </div>
        </div>

        <div style={styles.formGrid}>
          <div style={styles.fieldWrap}>
            <label style={styles.label}>{t("studioTools.commentToContentTool.fields.topic.label")}</label>
            <input
              type="text"
              value={topicValue}
              onChange={(e) => setSharedTopic?.(e.target.value)}
              placeholder={t("studioTools.commentToContentTool.fields.topic.placeholder")}
              style={styles.input}
            />
            <FieldHint text={t("studioTools.commentToContentTool.fields.topic.hint")} />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>{t("studioTools.commentToContentTool.fields.platform.label")}</label>
            <select
              value={selectedPlatform}
              onChange={(e) => setSharedPlatform?.(e.target.value)}
              style={styles.input}
            >
              {platformOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(`studioTools.commentToContentTool.platformOptions.${option.labelKey}`, option.fallback)}
                </option>
              ))}
            </select>
            <FieldHint text={t("studioTools.commentToContentTool.fields.platform.hint")} />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>
              {t("studioTools.commentToContentTool.fields.outputType.label")}
            </label>
            <select
              value={outputType}
              onChange={(e) => setOutputType(e.target.value)}
              style={styles.input}
            >
              {outputTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(
                    `studioTools.commentToContentTool.outputTypeOptions.${option.labelKey}`,
                    option.fallback
                  )}
                </option>
              ))}
            </select>
            <FieldHint text={t("studioTools.commentToContentTool.fields.outputType.hint")} />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>{t("studioTools.commentToContentTool.fields.tone.label")}</label>
            <select value={tone} onChange={(e) => setTone(e.target.value)} style={styles.input}>
              {toneOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(`studioTools.commentToContentTool.toneOptions.${option.labelKey}`, option.fallback)}
                </option>
              ))}
            </select>
            <FieldHint text={t("studioTools.commentToContentTool.fields.tone.hint")} />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>
              {t("studioTools.commentToContentTool.fields.targetAudience.label")}
            </label>
            <input
              type="text"
              value={targetAudience}
              onChange={(e) => setTargetAudience(e.target.value)}
              placeholder={t("studioTools.commentToContentTool.fields.targetAudience.placeholder")}
              style={styles.input}
            />
            <FieldHint text={t("studioTools.commentToContentTool.fields.targetAudience.hint")} />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>{t("studioTools.commentToContentTool.fields.goal.label")}</label>
            <input
              type="text"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder={t("studioTools.commentToContentTool.fields.goal.placeholder")}
              style={styles.input}
            />
            <FieldHint text={t("studioTools.commentToContentTool.fields.goal.hint")} />
          </div>

          <div style={styles.fullWidthField}>
            <label style={styles.label}>{t("studioTools.commentToContentTool.fields.comment.label")}</label>
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder={t("studioTools.commentToContentTool.fields.comment.placeholder")}
              style={styles.textarea}
            />
            <FieldHint text={t("studioTools.commentToContentTool.fields.comment.hint")} />
          </div>

          <div style={styles.fullWidthField}>
            <label style={styles.label}>
              {t("studioTools.commentToContentTool.fields.extraContext.label")}
            </label>
            <textarea
              value={contentContext}
              onChange={(e) => setContentContext(e.target.value)}
              placeholder={t("studioTools.commentToContentTool.fields.extraContext.placeholder")}
              style={styles.textarea}
            />
            <FieldHint text={t("studioTools.commentToContentTool.fields.extraContext.hint")} />
          </div>
        </div>

        <ActionRow>
          <AIActionButton isDark={isDark}
            type="button"
            onClick={handleGenerate}
            disabled={loading}
          >
            {loading
              ? t("studioTools.commentToContentTool.actions.generating")
              : t("studioTools.commentToContentTool.actions.generate")}
          </AIActionButton>

          <UtilityButton isDark={isDark}
            type="button"
            onClick={clearResults}
          >
            {t("studioTools.commentToContentTool.actions.clearResults")}
          </UtilityButton>

          <UtilityButton isDark={isDark}
            type="button"
            onClick={resetAll}
          >
            {t("studioTools.commentToContentTool.actions.reset")}
          </UtilityButton>
        </ActionRow>

        <div style={{ marginTop: 10 }}>
          <span style={styles.mutedText}>{t("studioTools.commentToContentTool.tip")}</span>
        </div>

        {error ? <div style={styles.errorBox}>{error}</div> : null}
      </div>

      {loading ? (
        <ToolResultSkeleton isDark={isDark} count={3} />
      ) : result ? (
        <div style={styles.card}>
          <ResultCard
            colors={{
              border: isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)",
              cardBgStrong: isDark ? "rgba(2,6,23,0.56)" : "rgba(248,250,252,0.96)",
              shadowSoft: isDark
                ? "0 10px 24px rgba(2,6,23,0.22)"
                : "0 6px 18px rgba(15,23,42,0.05)",
            }}
            index={0}
          >
            <div style={styles.resultHeader}>
              <h3 style={styles.resultTitle}>{t("studioTools.commentToContentTool.results.title")}</h3>

              <ActionRow style={styles.resultActions}>
                <CopyButton
                  type="button"
                  copied={copied}
                  onClick={copyResult}
                >
                  {copied
                    ? t("studioTools.commentToContentTool.actions.copied")
                    : t("studioTools.commentToContentTool.actions.copy")}
                </CopyButton>

                <SaveStudioAssetButton
                  toolType="comment_to_content"
                  title={`${t("studioTools.commentToContentTool.badge")}${topicValue ? ` - ${topicValue}` : ""}`}
                  topic={topicValue || t("studioTools.commentToContentTool.badge")}
                  platform={selectedPlatform}
                  content={formattedText}
                  meta={{
                    commentText,
                    contentContext,
                    targetAudience,
                    goal,
                    outputType,
                    tone,
                  }}
                />
              </ActionRow>
            </div>

            <div style={styles.chipRow}>
              <span style={styles.chip}>
                {t(
                  `studioTools.commentToContentTool.platformOptions.${
                    platformOptions.find((item) => item.value === selectedPlatform)?.labelKey || "general"
                  }`,
                  "General"
                )}
              </span>
              <span style={styles.chip}>
                {t(
                  `studioTools.commentToContentTool.outputTypeOptions.${
                    outputTypeOptions.find((item) => item.value === outputType)?.labelKey || "reply"
                  }`,
                  "Reply"
                )}
              </span>
              <span style={styles.chip}>
                {t(
                  `studioTools.commentToContentTool.toneOptions.${
                    toneOptions.find((item) => item.value === tone)?.labelKey || "natural"
                  }`,
                  "Natural"
                )}
              </span>
              {topicValue ? <span style={styles.chip}>{topicValue}</span> : null}
            </div>

            {mainOutput ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>
                  {t("studioTools.commentToContentTool.resultLabels.output")}
                </h4>
                <div style={styles.outputBox}>
                  <p style={styles.paragraph}>{mainOutput}</p>
                </div>
              </div>
            ) : null}

            {result?.angle ||
            result?.content_angle ||
            result?.idea_angle ||
            result?.strategy_angle ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>
                  {t("studioTools.commentToContentTool.resultLabels.angle")}
                </h4>
                <p style={styles.paragraph}>
                  {result?.angle ||
                    result?.content_angle ||
                    result?.idea_angle ||
                    result?.strategy_angle}
                </p>
              </div>
            ) : null}

            {result?.summary || result?.overview || result?.analysis || result?.explanation ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>
                  {t("studioTools.commentToContentTool.resultLabels.summary")}
                </h4>
                <p style={styles.paragraph}>
                  {result?.summary || result?.overview || result?.analysis || result?.explanation}
                </p>
              </div>
            ) : null}

            {ideas.length ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>
                  {t("studioTools.commentToContentTool.resultLabels.ideas")}
                </h4>
                <ul style={styles.list}>
                  {ideas.map((item, index) => (
                    <li key={`idea-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {notes.length ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>
                  {t("studioTools.commentToContentTool.resultLabels.notes")}
                </h4>
                <ul style={styles.list}>
                  {notes.map((item, index) => (
                    <li key={`note-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {!formattedText ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>
                  {t("studioTools.commentToContentTool.resultLabels.rawResult")}
                </h4>
                <p style={styles.paragraph}>{JSON.stringify(result, null, 2)}</p>
              </div>
            ) : null}
          </ResultCard>
        </div>
      ) : null}
    </div>
    </MotionSurface>
  );
};

export default CommentToContentTool;